import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Sparkles, AlertCircle, CheckCircle, Loader2 } from "lucide-react";

const SPOTLIGHT_PRICE = 149;

export const SpotlightCreditsSection = () => {
  const { t } = useTranslation();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [hasActiveSpotlight, setHasActiveSpotlight] = useState(false);

  useEffect(() => {
    loadCredits();
  }, []);

  const loadCredits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load available (unused, not expired) credits
      const { data: creditsData } = await supabase
        .from("spotlight_credits")
        .select("amount")
        .eq("user_id", user.id)
        .eq("used", false)
        .or("expires_at.is.null,expires_at.gt.now()");

      const totalCredits = creditsData?.reduce((sum, c) => sum + parseFloat(c.amount.toString()), 0) || 0;
      setCredits(totalCredits);

      // Check if user has active spotlight
      const { data: spotlightData } = await supabase
        .from("featured_professionals")
        .select("id")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .gt("end_date", new Date().toISOString())
        .limit(1);

      setHasActiveSpotlight((spotlightData?.length || 0) > 0);
    } catch (error) {
      console.error("Error loading credits:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateSpotlight = async () => {
    if (credits < SPOTLIGHT_PRICE) {
      toast.error(t("creator.spotlightCredits.insufficientCredits"));
      return;
    }

    setActivating(true);
    try {
      const { data, error } = await supabase.functions.invoke("use-spotlight-credits", {
        body: { amount: SPOTLIGHT_PRICE }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(t("creator.spotlightCredits.activationSuccess"));
      loadCredits();
    } catch (error: any) {
      console.error("Error activating spotlight:", error);
      toast.error(error.message || t("creator.spotlightCredits.activationError"));
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <BrutalCard className="p-6 mb-8">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </BrutalCard>
    );
  }

  return (
    <BrutalCard className="p-6 mb-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-primary/20 rounded-lg">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        
        <div className="flex-1">
          <h2 className="font-heading text-xl font-bold mb-2">
            {t("creator.spotlightCredits.title")}
          </h2>
          
          {/* Credits Balance */}
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-4xl font-bold text-primary">€{credits.toFixed(2)}</span>
            <span className="text-muted-foreground">{t("creator.spotlightCredits.available")}</span>
          </div>

          {/* Spotlight Package Info */}
          <div className="bg-card border-[2px] border-dark p-4 rounded mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold">{t("creator.spotlightCredits.packageName")}</span>
              <span className="text-xl font-bold">€{SPOTLIGHT_PRICE}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("creator.spotlightCredits.packageDescription")}
            </p>
          </div>

          {/* Status and Actions */}
          {hasActiveSpotlight ? (
            <div className="flex items-center gap-2 p-3 bg-success/10 border-[2px] border-success rounded">
              <CheckCircle className="w-5 h-5 text-success" />
              <span className="font-bold text-success-foreground">
                {t("creator.spotlightCredits.alreadyActive")}
              </span>
            </div>
          ) : credits >= SPOTLIGHT_PRICE ? (
            <BrutalButton 
              onClick={handleActivateSpotlight} 
              disabled={activating}
              className="w-full"
            >
              {activating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("common.loading")}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t("creator.spotlightCredits.activate")}
                </>
              )}
            </BrutalButton>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-muted border-[2px] border-muted-foreground/20 rounded">
              <AlertCircle className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t("creator.spotlightCredits.needMore", { 
                  needed: (SPOTLIGHT_PRICE - credits).toFixed(2) 
                })}
              </span>
            </div>
          )}

          {/* Important Notice */}
          <div className="mt-4 p-3 bg-warning/10 border-[2px] border-warning rounded">
            <p className="text-xs text-muted-foreground">
              <span className="font-bold">⚠️ {t("creator.spotlightCredits.notWithdrawable")}</span>
              <br />
              {t("creator.spotlightCredits.creditsOnlyForSpotlight")}
            </p>
          </div>
        </div>
      </div>
    </BrutalCard>
  );
};
